import {
  QueryFunction,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { getInjectedArtist } from "utils/injectedData";

import * as api from "./fetch/fetchWrapper";
import {
  QUERY_KEY_ARTISTS,
  QUERY_KEY_MERCH,
  QUERY_KEY_SALES,
  QUERY_KEY_TRACK_GROUPS,
} from "./queryKeys";

const fetchArtist: QueryFunction<
  Artist,
  ["fetchArtist", { artistSlug?: string }]
> = ({ queryKey: [_, { artistSlug }], signal }) => {
  return api
    .get<{
      result: Artist;
    }>(`v1/artists/${artistSlug}?includeDefaultTier=true`, {
      signal,
    })
    .then((r) => r.result);
};

export function queryArtist(opts: { artistSlug?: string }) {
  return queryOptions({
    queryKey: ["fetchArtist", { artistSlug: opts.artistSlug }],
    queryFn: fetchArtist,
    initialData: () =>
      opts.artistSlug ? getInjectedArtist(opts.artistSlug) : undefined,
    enabled: !!opts.artistSlug,
  });
}

const fetchCataloguePrice: QueryFunction<
  { price: number | null },
  ["fetchCataloguePrice", { artistId?: number }, ...any]
> = ({ queryKey: [_, { artistId }], signal }) => {
  return api
    .get<{
      result: { price: number | null };
    }>(`v1/artists/${artistId}/purchaseCatalogue`, { signal })
    .then((r) => r.result);
};

export function queryCataloguePrice(opts: { artistId?: number }) {
  return queryOptions({
    queryKey: ["fetchCataloguePrice", opts, QUERY_KEY_ARTISTS],
    queryFn: fetchCataloguePrice,
    enabled: !!opts.artistId,
  });
}

export type ManagedArtist = Pick<
  Artist,
  "id" | "name" | "urlSlug" | "isLabelProfile" | "avatar"
> & { relationship: "owner" | "manager" };

const fetchManagedArtists: QueryFunction<
  { results: ManagedArtist[] },
  ["fetchManagedArtists", {}, ...any]
> = ({ queryKey: [_], signal }) => {
  return api.get(`v1/manage/artists`, { signal });
};

export function queryManagedArtists(opts?: {}) {
  return queryOptions({
    queryKey: ["fetchManagedArtists", opts ?? {}, QUERY_KEY_ARTISTS],
    queryFn: fetchManagedArtists,
  });
}

const fetchUserArtists: QueryFunction<
  { results: Artist[] },
  ["fetchUserArtists", { userId?: number }, ...any]
> = ({ queryKey: [_, { userId }], signal }) => {
  return api.get(`v1/users/${userId}/artists`, { signal });
};

export function queryUserArtists(opts: { userId?: number }) {
  return queryOptions({
    queryKey: ["fetchUserArtists", opts, QUERY_KEY_ARTISTS],
    queryFn: fetchUserArtists,
    enabled: !!opts.userId,
  });
}

const fetchManagedArtistTrackGroups: QueryFunction<
  { results: TrackGroup[] },
  [
    "fetchUserTrackGroups",
    { artistId?: number; includeLabelReleases?: boolean },
    ...any,
  ]
> = ({ queryKey: [_, { artistId, includeLabelReleases }], signal }) => {
  const params = includeLabelReleases ? "?includeLabelReleases=true" : "";
  return api.get(`v1/manage/artists/${artistId}/trackGroups${params}`, {
    signal,
  });
};

export function queryManagedArtistTrackGroups(opts: {
  artistId?: number;
  includeLabelReleases?: boolean;
}) {
  return queryOptions({
    queryKey: ["fetchUserTrackGroups", opts, QUERY_KEY_TRACK_GROUPS],
    queryFn: fetchManagedArtistTrackGroups,
    enabled: !!opts.artistId,
  });
}

const fetchManagedArtistMerch: QueryFunction<
  { results: Merch[] },
  ["fetchUserMerch", { artistId?: number }, ...any]
> = ({ queryKey: [_, { artistId }], signal }) => {
  return api.get(`v1/manage/artists/${artistId}/merch`, { signal });
};

export function queryManagedArtistMerch(opts: { artistId?: number }) {
  return queryOptions({
    queryKey: ["fetchUserMerch", opts, QUERY_KEY_MERCH],
    queryFn: fetchManagedArtistMerch,
  });
}

export type TerminalReader = {
  id: string;
  label: string | null;
  deviceType: string;
  status: string | null;
};

const fetchManagedArtistReaders: QueryFunction<
  { results: TerminalReader[] },
  ["fetchManagedArtistReaders", { artistId?: number }]
> = ({ queryKey: [_, { artistId }], signal }) => {
  return api.get(`v1/manage/artists/${artistId}/readers`, { signal });
};

export function queryManagedArtistReaders(opts: { artistId?: number }) {
  return queryOptions({
    queryKey: ["fetchManagedArtistReaders", opts],
    queryFn: fetchManagedArtistReaders,
    enabled: !!opts.artistId,
  });
}

const fetchManagedArtistSubscriptionTiers: QueryFunction<
  { results: ArtistSubscriptionTier[] },
  [
    "fetchManagedArtistSubscriptionTiers",
    { artistId?: number; includeDefault?: boolean },
    ...any,
  ]
> = ({ queryKey: [_, { artistId, includeDefault }], signal }) => {
  return api.get(
    `v1/manage/artists/${artistId}/subscriptionTiers?${includeDefault ? "includeDefault=true" : ""}`,
    { signal }
  );
};

export function queryManagedArtistSubscriptionTiers(opts: {
  artistId?: number;
  includeDefault?: boolean;
}) {
  return queryOptions({
    queryKey: [
      "fetchManagedArtistSubscriptionTiers",
      opts,
      QUERY_KEY_TRACK_GROUPS,
    ],
    queryFn: fetchManagedArtistSubscriptionTiers,
  });
}

const fetchManagedArtistSubscriptionTier: QueryFunction<
  ArtistSubscriptionTier,
  [
    "fetchManagedArtistSubscriptionTier",
    { artistId?: number; tierId?: number },
    ...any,
  ]
> = ({ queryKey: [_, { artistId, tierId }], signal }) => {
  return api
    .get<{
      result: ArtistSubscriptionTier;
    }>(`v1/manage/artists/${artistId}/subscriptionTiers/${tierId}`, { signal })
    .then((r) => r.result);
};

export function queryManagedArtistSubscriptionTier(opts: {
  artistId?: number;
  tierId?: number;
}) {
  return queryOptions({
    queryKey: [
      "fetchManagedArtistSubscriptionTier",
      opts,
      QUERY_KEY_TRACK_GROUPS,
    ],
    queryFn: fetchManagedArtistSubscriptionTier,
    enabled: Boolean(opts.artistId && opts.tierId),
  });
}

async function createSubscriptionTier(opts: {
  artistId: number;
  name: string;
}): Promise<{ result: ArtistSubscriptionTier }> {
  return api.post(`v1/manage/artists/${opts.artistId}/subscriptionTiers/`, {
    name: opts.name,
  });
}

export function useCreateSubscriptionTierMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createSubscriptionTier,
    async onSuccess(_, { artistId }) {
      await client.invalidateQueries({
        queryKey: [
          "fetchManagedArtistSubscriptionTiers",
          { artistId },
          QUERY_KEY_TRACK_GROUPS,
        ],
      });
    },
  });
}

const fetchArtistSupporters: QueryFunction<
  {
    results: {
      name: string;
      amount: number;
      message?: string;
      datePurchased: string;
    }[];
    total: number;
    totalAmount: number;
    totalSupporters: number;
  },
  [
    "fetchArtistSupporters",
    opts: {
      artistId: number;
      take?: number;
      skip?: number;
      trackGroupIds?: number[];
    },
    ...any,
  ]
> = ({ queryKey: [_, opts], signal }) => {
  const { artistId } = opts;
  const params = new URLSearchParams();

  if (opts.trackGroupIds) {
    opts.trackGroupIds.forEach((id) => {
      if (isFinite(id)) {
        params.append("trackGroupIds", String(id));
      }
    });
  }

  if (opts.take) {
    params.append("take", String(opts.take));
  }
  if (opts.skip) {
    params.append("skip", String(opts.skip));
  }
  return api.get(`v1/artists/${artistId}/supporters/?${params.toString()}`, {
    signal,
  });
};

export function queryArtistSupporters(opts: {
  artistId: number;
  take?: number;
  skip?: number;
  trackGroupIds?: number[];
}) {
  return queryOptions({
    queryKey: ["fetchArtistSupporters", opts, QUERY_KEY_SALES],
    queryFn: fetchArtistSupporters,
  });
}

export type ArtistSubscriber = {
  id: number;
  user: User;
  createdAt: string;
  amount: number;
  artistSubscriptionTier: ArtistSubscriptionTier;
  artistUserSubscriptionCharges?: {
    id: string;
    transactionId?: string;
    transaction?: {
      platformCut: number | null;
      stripeCut: number | null;
      paymentStatus: "PENDING" | "COMPLETED" | "FAILED";
    } | null;
  }[];
};

const fetchManageArtistSubscribers: QueryFunction<
  { results: ArtistSubscriber[] },
  ["fetchManageArtistSubscribers", { artistId?: number }, ...any]
> = ({ queryKey: [_, { artistId }], signal }) => {
  return api.get(`v1/manage/artists/${artistId}/subscribers`, { signal });
};

export function queryManageArtistSubscribers(opts: { artistId?: number }) {
  return queryOptions({
    queryKey: ["fetchManageArtistSubscribers", opts, QUERY_KEY_ARTISTS],
    queryFn: fetchManageArtistSubscribers,
    // artistId is undefined until the parent artist query resolves; without
    // this the request goes out as ".../artists/undefined/subscribers".
    enabled: !!opts.artistId,
  });
}

async function cancelArtistSubscriber(opts: {
  artistId: number;
  subscriptionId: number;
}) {
  return api.del(
    `v1/manage/artists/${opts.artistId}/subscribers/${opts.subscriptionId}`
  );
}

export function useCancelArtistSubscriberMutation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: cancelArtistSubscriber,
    async onSuccess(_, { artistId }) {
      await client.invalidateQueries({
        queryKey: ["fetchManageArtistSubscribers", { artistId }],
      });
    },
  });
}

export type ArtistQueryOptions = {
  skip?: number;
  take?: number;
  orderBy?: "random";
  name?: string;
  isLabel?: boolean;
  locationSlug?: string;
  includeUnpublished?: boolean;
};

const fetchArtists: QueryFunction<
  { results: Artist[]; total?: number },
  ["fetchArtists", ArtistQueryOptions, ...any]
> = ({
  queryKey: [
    _,
    { skip, take, orderBy, name, locationSlug, isLabel, includeUnpublished },
  ],
  signal,
}) => {
  const params = new URLSearchParams();
  if (skip) params.append("skip", String(skip));
  if (take) params.append("take", String(take));
  if (orderBy) params.append("orderBy", orderBy);
  if (name) params.append("name", name);
  if (locationSlug) params.append("locationSlug", locationSlug);
  if (isLabel !== undefined) params.append("isLabel", String(isLabel));
  if (includeUnpublished) params.append("includeUnpublished", "true");

  return api.get(`v1/artists?${params}`, { signal });
};

export function queryArtists(opts: ArtistQueryOptions) {
  return queryOptions({
    queryKey: ["fetchArtists", opts, QUERY_KEY_ARTISTS],
    queryFn: fetchArtists,
  });
}
